import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { Star, ArrowLeft, ArrowRight } from 'lucide-react';

import img1 from '../../assets/home/img1.jpg';
import img2 from '../../assets/home/img2.jpg';
import img3 from '../../assets/home/img3.jpg';
import img4 from '../../assets/home/img4.jpg';
import img5 from '../../assets/home/img5.jpg';

const Reviews = () => {
  const baseReviews = [
    { name: "Marc Lefebvre", role: "CTO, TechCorp Inc.", image: img1, text: "L'audit OSINT réalisé par Nodixs a révélé des informations critiques que nous pensions sécurisées.", rating: 5 },
    { name: "Sophie Martin", role: "DSI, Global Logistics", image: img2, text: "Leur méthodologie de Pentest est la plus rigoureuse que j'ai pu voir. Les rapports sont clairs.", rating: 5 },
    { name: "Julien Vasseur", role: "Fondateur, ShopSafe", image: img3, text: "Grâce à Nodixs, nous avons pu sécuriser notre plateforme e-commerce avant son lancement.", rating: 5 },
    { name: "Amélie Roux", role: "RSSI, FinTech Solutions", image: img4, text: "Une expertise rare en cybersécurité offensive. Analyse des vecteurs d'attaque impressionnante.", rating: 5 },
    { name: "Thomas Dubois", role: "Directeur Technique, InnovA", image: img5, text: "L'accompagnement post-audit est incroyable. Ils nous aident réellement à combler les failles.", rating: 5 }
  ];

  // On triple le tableau pour permettre un loop fluide dans les deux sens
  const allReviews = [...baseReviews, ...baseReviews, ...baseReviews];
  
  // On commence au milieu (index de la deuxième série)
  const [currentIndex, setCurrentIndex] = useState(baseReviews.length);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef(null);

  // Gestion du responsive pour le décalage
  const getVisibleCards = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1024) return 3;
      if (window.innerWidth >= 768) return 2;
    }
    return 1;
  };

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(prev => prev + 1);
  };

  const handlePrev = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(prev => prev - 1);
  };

  // Effet pour gérer la boucle "invisible"
  useEffect(() => {
    const numBase = baseReviews.length;
    
    // Si on dépasse la fin de la section du milieu
    if (currentIndex >= numBase * 2) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setCurrentIndex(numBase);
      }, 500); // Doit correspondre au temps de transition
      return () => clearTimeout(timer);
    }

    // Si on dépasse le début de la section du milieu
    if (currentIndex < numBase) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setCurrentIndex(numBase * 2 - 1);
      }, 500);
      return () => clearTimeout(timer);
    }

    // Fin de l'animation normale
    const timer = setTimeout(() => setIsAnimating(false), 500);
    return () => clearTimeout(timer);
  }, [currentIndex, baseReviews.length]);

  return (
    <section className="w-full bg-[#F6FAFD] py-16 px-6 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        
        {/* Header - Identique à ton image */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-[#0A1931] text-[32px] md:text-[40px] font-black leading-tight mb-6">
              Ils nous font confiance
            </h2>
            <p className="text-[#1A3D63]/70 text-[16px] font-medium">
              Découvrez pourquoi les leaders de l'industrie choisissent Nodixs.
            </p>
          </div>

          <div className="flex gap-4">
            <button 
              onClick={handlePrev}
              disabled={isAnimating}
              className="w-12 h-12 rounded-full border border-[#E5EDF5] bg-white flex items-center justify-center text-[#0A1931] hover:bg-[#3b82f6] hover:text-white transition-all shadow-sm disabled:opacity-50"
            >
              <ArrowLeft size={20} />
            </button>
            <button 
              onClick={handleNext}
              disabled={isAnimating}
              className="w-12 h-12 rounded-full border border-[#E5EDF5] bg-white flex items-center justify-center text-[#0A1931] hover:bg-[#3b82f6] hover:text-white transition-all shadow-sm disabled:opacity-50"
            >
              <ArrowRight size={20} />
            </button>
          </div>
        </div>

        {/* Slider avec calcul dynamique du gap et de la largeur */}
        <div className="relative" ref={containerRef}>
          <motion.div 
            className="flex gap-6"
            animate={{ 
              x: `calc(-${currentIndex * (100 / getVisibleCards())}% - ${currentIndex * (24 / getVisibleCards())}px)` 
            }}
            transition={{ 
              duration: isAnimating ? 0.5 : 0, 
              ease: [0.45, 0, 0.55, 1] 
            }}
          >
            {allReviews.map((review, i) => (
              <div
                key={i}
                className="min-w-full md:min-w-[calc(50%-12px)] lg:min-w-[calc(33.333%-16px)]"
              >
                <div className="bg-white p-8 rounded-[2rem] border border-[#E5EDF5] shadow-sm flex flex-col h-full hover:shadow-md transition-shadow">
                  <div className="flex gap-1 mb-6">
                    {[...Array(review.rating)].map((_, idx) => (
                      <Star key={idx} size={18} className="fill-[#3b82f6] text-[#3b82f6]" />
                    ))}
                  </div>
                  
                  <p className="text-[#1A3D63]/80 text-[15px] leading-relaxed mb-8 flex-grow">
                    "{review.text}"
                  </p>

                  <div className="flex items-center gap-4 pt-6 border-t border-[#F6FAFD]">
                    <div className="w-12 h-12 rounded-full bg-[#B3CFE5] overflow-hidden border border-[#E5EDF5]">
                      <img 
                        src={review.image} 
                        alt={review.name} 
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <h4 className="text-[#0A1931] font-black text-[15px]">{review.name}</h4>
                      <p className="text-[#1A3D63]/60 text-[13px] font-bold uppercase tracking-wider">{review.role}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;